import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getBrandBySlug, getBrandByCustomDomain, getBrandBySubdomain, getDefaultBrand, Brand } from '@/lib/api/services/brands';

// ============ Types ============

type BrandDetectionResult = { type: 'subdomain' | 'custom_domain' | 'path'; value: string } | null;

export interface BrandConfig {
  id: string;
  name: string;
  slug: string;
  industry?: string | null;
  logoUrl?: string | null;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  themeConfig: Record<string, unknown>;
  status: 'active' | 'trial' | 'suspended' | 'archived';
  planTier: 'starter' | 'professional' | 'enterprise';
  isPlatformOwner: boolean;
}

export interface BrandContextType {
  brand: BrandConfig | null;
  isLoading: boolean;
  error: Error | null;
  isBrandAdmin: boolean;
  isPlatformBrand: boolean;
  isPlatformMode: boolean;
  refetchBrand: () => void;
}

// ============ Default Brand Config ============

const DEFAULT_BRAND_CONFIG: BrandConfig = {
  id: 'default',
  name: 'MindCo Health',
  slug: 'mindco',
  industry: 'mental_health',
  logoUrl: '/mindco-logo.svg',
  primaryColor: '#9b87f5',
  secondaryColor: '#7E69AB',
  accentColor: '#F97316',
  themeConfig: {},
  status: 'active',
  planTier: 'enterprise',
  isPlatformOwner: true,
};

// ============ Context ============

const BrandContext = createContext<BrandContextType | undefined>(undefined);

// ============ Helper Functions ============

function transformBrandToConfig(brand: Brand): BrandConfig {
  const settings = brand.settings as Record<string, unknown> | null;
  return {
    id: brand.id,
    name: brand.name,
    slug: brand.slug,
    industry: brand.industry,
    logoUrl: brand.logo_url,
    primaryColor: brand.primary_color || DEFAULT_BRAND_CONFIG.primaryColor,
    secondaryColor: brand.secondary_color || DEFAULT_BRAND_CONFIG.secondaryColor,
    accentColor: brand.accent_color || DEFAULT_BRAND_CONFIG.accentColor,
    themeConfig: (brand.theme_config as Record<string, unknown>) || {},
    status: brand.status,
    planTier: brand.plan_tier,
    isPlatformOwner: settings?.is_platform_owner === true,
  };
}

// Platform domains - these should use default brand
const PLATFORM_DOMAINS = [
  'mindcohealth.com',
  'mindco.health',
  'lovable.app',
  'lovableproject.com',
];

// Check if we're on the platform landing domain
function isPlatformDomain(): boolean {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname.toLowerCase();

  // Support production, preview, and development environments
  return (
    hostname === 'platform.mindcohealth.com' ||
    hostname.endsWith('.lovableproject.com') ||
    hostname.endsWith('.lovable.app') ||
    hostname === 'localhost' ||
    hostname === '127.0.0.1'
  );
}

// Detect brand from current hostname and pathname
// Returns { type: 'subdomain' | 'custom_domain' | 'path', value: string } or null
function detectBrandFromHost(): BrandDetectionResult {
  if (typeof window === 'undefined') return null;

  const hostname = window.location.hostname.toLowerCase();
  const pathname = window.location.pathname;

  // Local development - no brand detection
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return null;
  }

  // Lovable preview domains - treat as platform mode (no brand detection)
  if (hostname.endsWith('.lovableproject.com') || hostname.endsWith('.lovable.app')) {
    return null;
  }

  // Path-based brand detection for platform domain
  if (hostname === 'platform.mindcohealth.com') {
    const pathSegments = pathname.split('/').filter(Boolean);
    const brandSlug = pathSegments[0];

    // Skip if it's /b/:brandSlug pattern (handled separately)
    if (brandSlug === 'b') {
      return null;
    }

    // Skip system routes
    const systemRoutes = ['auth', 'admin', 'reset-password', 'verify-email', 'no-products'];
    if (brandSlug && !systemRoutes.includes(brandSlug)) {
      return { type: 'path', value: brandSlug };
    }

    return null; // Platform landing
  }

  // Check if this is a known platform domain
  const isPlatformDomainMatch = PLATFORM_DOMAINS.some(domain =>
    hostname === domain || hostname.endsWith(`.${domain}`)
  );

  if (isPlatformDomainMatch) {
    // Check for subdomain pattern: {brand}.platform.com
    const parts = hostname.split('.');
    if (parts.length >= 3) {
      const subdomain = parts[0];
      // Skip common subdomains (including platform, webapp)
      if (!['www', 'app', 'api', 'admin', 'staging', 'preview', 'platform', 'webapp'].includes(subdomain)) {
        return { type: 'subdomain', value: subdomain };
      }
    }
    return null;
  }

  // Not a platform domain - treat as custom domain
  // Remove www. prefix if present
  const cleanHostname = hostname.replace(/^www\./, '');
  return { type: 'custom_domain', value: cleanHostname };
}

// ============ Provider ============

interface BrandProviderProps {
  children: ReactNode;
  brandSlug?: string; // Optional override for testing
}

export const BrandProvider: React.FC<BrandProviderProps> = ({ children, brandSlug }) => {
  const [detectedBrand] = useState<BrandDetectionResult>(() => 
    brandSlug ? { type: 'subdomain', value: brandSlug } : detectBrandFromHost()
  );

  // Fetch brand data
  const {
    data: brandResult,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['brand', detectedBrand?.type, detectedBrand?.value],
    queryFn: async () => {
      // If no brand detected, use default brand
      if (!detectedBrand) {
        const result = await getDefaultBrand();
        if (!result.success) throw new Error(result.error.message);
        return result.data;
      }

      let result;

      if (detectedBrand.type === 'custom_domain') {
        // Try to find brand by custom domain
        result = await getBrandByCustomDomain(detectedBrand.value);
      } else if (detectedBrand.type === 'path') {
        // Try to find brand by slug (path-based routing)
        result = await getBrandBySlug(detectedBrand.value);
      } else {
        // Try subdomain match
        result = await getBrandBySubdomain(detectedBrand.value);
      }

      if (!result.success) throw new Error(result.error.message);

      // If not found, fall back to default
      if (!result.data) {
        const defaultResult = await getDefaultBrand();
        if (!defaultResult.success) throw new Error(defaultResult.error.message);
        return defaultResult.data;
      }

      return result.data;
    },
    staleTime: 1000 * 60 * 10, // 10 minutes - brands don't change often
    gcTime: 1000 * 60 * 30, // 30 minutes cache
  });

  // Transform to config
  const brand = brandResult ? transformBrandToConfig(brandResult) : null;
  const isPlatformBrand = brand?.isPlatformOwner || !detectedBrand;

  // Calculate if we're in platform mode (platform domain with no brand)
  const platformMode = isPlatformDomain() && !detectedBrand;

  // Apply brand CSS variables
  useEffect(() => {
    if (!brand) return;

    const root = document.documentElement;

    // Only apply brand colors if not platform owner (whitelabel brands)
    if (!brand.isPlatformOwner) {
      root.style.setProperty('--brand-primary', brand.primaryColor);
      root.style.setProperty('--brand-secondary', brand.secondaryColor);
      root.style.setProperty('--brand-accent', brand.accentColor);
    }

    // Clean up on unmount
    return () => {
      root.style.removeProperty('--brand-primary');
      root.style.removeProperty('--brand-secondary');
      root.style.removeProperty('--brand-accent');
    };
  }, [brand]);

  const value: BrandContextType = {
    brand: brand || DEFAULT_BRAND_CONFIG,
    isLoading,
    error: error as Error | null,
    isBrandAdmin: false, // Will be enhanced with auth integration
    isPlatformBrand,
    isPlatformMode: platformMode,
    refetchBrand: refetch,
  };

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
};

// ============ Hook ============

export const useBrand = (): BrandContextType => {
  const context = useContext(BrandContext);
  if (context === undefined) {
    throw new Error('useBrand must be used within a BrandProvider');
  }
  return context;
};

// ============ Utility Hook ============

// Get brand-aware theme colors
export const useBrandTheme = () => {
  const { brand } = useBrand();
  
  return {
    primaryColor: brand?.primaryColor || DEFAULT_BRAND_CONFIG.primaryColor,
    secondaryColor: brand?.secondaryColor || DEFAULT_BRAND_CONFIG.secondaryColor,
    accentColor: brand?.accentColor || DEFAULT_BRAND_CONFIG.accentColor,
    logoUrl: brand?.logoUrl || DEFAULT_BRAND_CONFIG.logoUrl,
  };
};
